// 1. **Google Map Integration**: The component integrates Google Maps using the `@react-google-maps/api` library, rendering a map with markers based on store data. The map center is dynamically updated based on the selected store or a search query.

// 2. **Search Functionality**: Users can search for stores by town, postcode, or store name using a search input field. The map updates with the location of the store matching the search query.

// 3. **Dynamic Marker and InfoWindow**: Markers are displayed on the map for each store. When a marker is clicked, an `InfoWindow` opens with detailed store information, such as the store's name, address, phone number, and email.

// 4. **Handling State and Props**: The component manages several states, including the map center, search input, and a flag indicating if the map has loaded. It also receives `storeData`, `selectedPlace`, and `handleSelectedPlace` as props to control the store's location and details.

// 5. **Conditional Rendering**: The map and its markers are conditionally rendered only on the client side (`isClient` state). The component also uses a flag to ensure the map is loaded before enabling certain functionalities like search and store selection.

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { GoogleMap, LoadScript, MarkerF, InfoWindowF, useLoadScript, Libraries } from '@react-google-maps/api';
import Button from 'components/commercetools-ui/atoms/button';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Store } from '../../../../../types/product/store';
import CustomOverlay from './CustomOverlay';
import useMobileDevice from 'helpers/hooks/useMobileDevice';

import { calculateDistanceInMiles } from './findStore';
import DownICon from 'public/icons/ag-icons/down.svg';
import Loading from 'components/commercetools-ui/atoms/loading';

import StoreBadges from './storeBadges';
import { getTodayTimings } from 'helpers/utils/storeTimings';
import { getWeekwiseTimingsFromWeekdayText } from 'helpers/utils/getWeekwiseTimingsFromWeekdayText';

const mapContainerStyle = {
  width: '100%',
  height: '100vh',
  flex: 1,
};

interface SelectedPlace {
  id?: string;
  name: string;
  location: {
    lat: number;
    lng: number;
  };
}
// Define or import getStoreBadges at the top of NeighborhoodDiscovery.tsx
const getStoreBadges = (store: any, amplienceStores: any, badges: any) => {
  if (!amplienceStores || !badges) return [];
  const amplienceStore = Array.isArray(amplienceStores)
    ? amplienceStores?.find((s: any) => s?.storeKey === store?.ID)
    : null;
  if (!amplienceStore || !amplienceStore?.storeBadges) return [];
  return amplienceStore?.storeBadges
    ?.map((badgeId: string) => badges?.find((badge: any) => badge?.id === badgeId))
    ?.filter(Boolean);
};
interface NeighborhoodDiscoveryProps {
  storeData: Store[];
  selectedPlace: SelectedPlace | undefined;
  handleSelectedPlace: (place: SelectedPlace, originalQuery?: string) => void;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  userLocation: any;
  setUserLocation:
    | React.Dispatch<
        React.SetStateAction<{
          lat: number;
          lng: number;
        }>
      >
    | any;

  query?: string;
  originalSearchQuery?: string;
  setSearchInput: any;
  searchInput: any;
  setPreservedQuery: any;
  preservedQuery: any;
  setMapCenter: any;
  mapCenter: any;
  mapZoom: any;
  setMapZoom: any;
  amplenceStores?: any;
  badges?: any;
  amplienceStores?: any;
  getStoreBadges?: any;
  isUltraLargeTablet: boolean;
  isMobile: boolean;
  selectedLocale: string;
  getSVGElement?: any;
  clickAndCollect?: any;
  isCitySearch?: boolean;
  setCurrentLocationClicked?: any;
  setSlectedPlace?: any;
  storeTimingsData?: any;
}
function simpleDistance(a: string, b: string): number {
  if (a === b) return 0;
  const len = Math?.max(a?.length, b?.length);
  let diff = 0;
  for (let i = 0; i < len; i++) {
    if (a[i] !== b[i]) diff++;
  }
  return diff;
}

const normalize = (str = '') =>
  str
    ?.normalize('NFD')
    ?.replace(/[\u0300-\u036f]/g, '')
    ?.trim()
    ?.toLowerCase();

export const getFirstPlacePhotoUrl = (store: any, apiKey: string | undefined) => {
  const photos = store?.placeDetails?.photos;
  if (!photos || photos?.length === 0) {
    return null; // or fallback image URL
  }

  const firstPhoto = photos?.[0];
  const photoReference = firstPhoto?.photo_reference;

  if (!photoReference) {
    return null;
  }

  return `https://maps.googleapis.com/maps/api/place/photo?maxwidth=800&photoreference=${photoReference}&key=${apiKey}`;
};

export const findMatchingStore = (storeData: Store[], input: string): Store | undefined => {
  const normalizedInput = normalize(input);

  const exactMatch = storeData?.find((store) =>
    [store?.name, store?.city, store?.postalCode, store?.phone]?.some((field) => normalize(field) === normalizedInput),
  );
  if (exactMatch) return exactMatch;

  let bestMatch: Store | undefined;
  let bestScore = 3;
  for (const store of storeData) {
    const fields = [store?.name, store?.city, store?.postalCode, store?.phone];
    for (const field of fields) {
      const score = simpleDistance(normalize(field), normalizedInput);
      if (score < bestScore) {
        bestScore = score;
        bestMatch = store;
      }
    }
  }

  return bestMatch;
};
const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const toRad = (x: number) => (x * Math.PI) / 180;
  const R = 6371; // km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const getSuggestions = (
  value: string,
  storeData: Store[],
  userLocation?: { lat: number; lng: number } | null,
): (Store & { distanceKm?: number; matchType: 'name' | 'city' })[] => {
  const normalized = value?.toLowerCase();

  const storeMatches = storeData
    .filter((s) => s?.name?.toLowerCase().startsWith(normalized))
    .map((s) => ({ ...s, matchType: 'name' as const }));

  const cityMatches = storeData
    .filter((s) => s?.city?.toLowerCase().startsWith(normalized))
    .map((s) => ({ ...s, matchType: 'city' as const }));

  const allMatches = [...storeMatches, ...cityMatches];
  const uniqueMatches = Array.from(new Map(allMatches?.map((s) => [s?.ID, s]))?.values());

  const withDistance = (s: any & { matchType: 'name' | 'city' }) => {
    if (userLocation) {
      const dist = haversineDistance(userLocation?.lat, userLocation?.lng, s?.latitude, s?.longitude);
      return { ...s, distanceKm: Math?.round(dist * 10) / 10 };
    }
    return { ...s };
  };
  const matchesWithDist = uniqueMatches?.map(withDistance);

  matchesWithDist?.sort((a, b) => {
    if (a?.matchType !== b?.matchType) {
      return a?.matchType === 'name' ? -1 : 1;
    }
    if (userLocation && a?.distanceKm != null && b?.distanceKm != null) {
      return a?.distanceKm - b?.distanceKm;
    }

    return (a?.name || a?.city)?.localeCompare(b?.name || b?.city);
  });

  return matchesWithDist?.slice(0, 5);
};

const NeighborhoodDiscovery = ({
  storeData,
  selectedPlace,
  handleSelectedPlace,
  isOpen,
  setIsOpen,
  userLocation,
  setUserLocation,
  query,
  originalSearchQuery,
  setSearchInput,
  searchInput,
  setPreservedQuery,
  preservedQuery,
  setMapCenter,
  mapCenter,
  mapZoom,
  setMapZoom,
  badges,
  amplienceStores,
  getStoreBadges,
  isUltraLargeTablet,
  isMobile,
  selectedLocale,
  getSVGElement,
  clickAndCollect,
  isCitySearch,
  setCurrentLocationClicked,
  setSlectedPlace,
  storeTimingsData,
}: NeighborhoodDiscoveryProps) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const handleMapLoad = () => {
    setMapLoaded(true);
  };
  const libraries = useMemo<Libraries>(() => ['places', 'geometry'], []);
  const { isMobileDevice } = useMobileDevice();
  const [isClient, setIsClient] = useState(false);
  const queryPlace = query ? findMatchingStore(storeData, query) : undefined;
  const mapRef = useRef<google.maps.Map | null>(null);
  const phoneIcon = getSVGElement('Phone');
  const emailIcon = getSVGElement('Mail');
  const mapIcon = getSVGElement('map marker');
  const mobileMapICon = getSVGElement('Mobile Map Maker');
  const highLightedMapMarkerDeskTop = getSVGElement('highlighted map marker desktop');
  const highLightedMapMarkerMobile = getSVGElement('highlighted map marker mobile');
  const router = useRouter();
  const handleMarkerClick = (store: Store) => {
    handleSelectedPlace({
      id: store.ID,
      name: store?.name,
      location: {
        lat: store?.latitude,
        lng: store?.longitude,
      },
    });
    setPreservedQuery('');
    setIsOpen(true); // Always open overlay when marker is clicked
    if (mapRef.current) {
      mapRef.current.panTo({
        lat: store?.latitude,
        lng: store?.longitude,
      });

      mapRef.current.setZoom(14);
      setMapZoom(14);
    }
  };

  const handleCloseClick = () => {
    setIsOpen(false);
  };
  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    // Skip auto-select when in city search mode; markers will represent all city stores
    if (isCitySearch) return;
    if (mapLoaded && query && queryPlace) {
      setSearchInput(query);
      if (query !== queryPlace?.name) {
        setPreservedQuery(query);
      }
      handleSelectedPlace(
        {
          id: queryPlace?.ID,
          name: queryPlace?.name,
          location: {
            lat: queryPlace?.latitude,
            lng: queryPlace?.longitude,
          },
        },
        query,
      );
    }
  }, [mapLoaded, query, queryPlace, handleSelectedPlace, isCitySearch]);

  useEffect(() => {
    // Effect to update map center
    if (selectedPlace) {
      if (!preservedQuery) {
        setSearchInput(selectedPlace?.name);
      }
      setMapCenter(selectedPlace?.location);
    } else if (storeData?.length > 0) {
      setMapCenter({
        lat: storeData[0]?.latitude,
        lng: storeData[0]?.longitude,
      });
    }
  }, [selectedPlace, storeData, preservedQuery]);
  useEffect(() => {
    if (originalSearchQuery && originalSearchQuery !== searchInput) {
      setPreservedQuery(originalSearchQuery);
      setSearchInput(originalSearchQuery);
    }
  }, [originalSearchQuery]);
  useEffect(() => {
    if (isCitySearch && storeData?.length > 0 && mapRef.current && window?.google?.maps) {
      const bounds = new window.google.maps.LatLngBounds();
      storeData.forEach((store) => {
        if (store?.latitude && store?.longitude) {
          bounds.extend(new window.google.maps.LatLng(store.latitude, store.longitude));
        }
      });
      mapRef.current.fitBounds(bounds);

      // Optional: limit zoom level to prevent over-zooming
      window.google.maps.event.addListenerOnce(mapRef.current, 'bounds_changed', () => {
        if ((mapRef.current?.getZoom() ?? 0) > 12) {
          mapRef.current?.setZoom(12);
        }
      });
    }
  }, [isCitySearch, storeData]);

  if (!isClient) {
    return <></>;
  }

  const infoDetails = storeData?.filter((store: Store) => store?.name === selectedPlace?.name);
  ////Amplience Stores Timings are mapped here
  // const timings = getTodayTimings(infoDetails?.[0]);

  const distance =
    userLocation && infoDetails?.[0]?.latitude && infoDetails?.[0]?.longitude
      ? calculateDistanceInMiles(
          userLocation?.lat,
          userLocation?.lng,
          infoDetails?.[0]?.latitude,
          infoDetails?.[0]?.longitude,
        )
      : null;
  //Amplience Stores Timings are mapped here
  // const weekwiseTimings = infoDetails?.[0] ? getWeekwiseTimingsFromWeekdayText(infoDetails[0]) : [];
  const storeBadges = infoDetails?.[0] ? getStoreBadges(infoDetails[0]) : [];
  const mapAPIKey = process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY;
  const amplienceStore =
    Array.isArray(amplienceStores) && infoDetails[0]
      ? amplienceStores.find((s: any) => s.storeKey === infoDetails[0].ID)
      : null;
  const csvStoreTiming = storeTimingsData?.[infoDetails[0]?.ID]?.timings;
  const timings = getTodayTimings(csvStoreTiming);
  const weekwiseTimings = getWeekwiseTimingsFromWeekdayText(csvStoreTiming);
  const g = amplienceStore?.imageActivationForAmplience ?? false;
  const googlePhotoUrl = getFirstPlacePhotoUrl(infoDetails?.[0], mapAPIKey);

  const ampliencePhotoUrl = amplienceStore?.storeImage
    ? `https://americangolf.a.bigcontent.io/v1/static/${amplienceStore?.storeImage?.name}`
    : null;

  const storeImageUrl = g ? ampliencePhotoUrl : '';
  const mapOptions: google.maps.MapOptions = {
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
    gestureHandling: isMobileDevice ? 'greedy' : 'auto',
    restriction: {
      latLngBounds: {
        north: 60.8566,
        south: 49.8466,
        west: -11.0,
        east: 2.5,
      },
      strictBounds: true,
    },
    styles: [
      {
        featureType: 'poi', // points of interest
        elementType: 'all',
        stylers: [{ visibility: 'off' }], // hide them
      },
      {
        featureType: 'transit',
        elementType: 'all',
        stylers: [{ visibility: 'off' }], // optional: hide bus/train stops
      },
    ],
  };

  return (
    <LoadScript googleMapsApiKey={`${mapAPIKey}`} libraries={libraries}>
      <div className="relative flex">
        {isClient && (
          <GoogleMap
            id="store-map"
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={mapZoom}
            onLoad={(map) => {
              mapRef.current = map;
              if (window?.google?.maps) {
                if (!searchInput && !userLocation && storeData?.length > 0) {
                  const bounds = new window.google.maps.LatLngBounds();

                  storeData.forEach((store) => {
                    if (store?.latitude && store?.longitude) {
                      bounds.extend(new window.google.maps.LatLng(store?.latitude, store?.longitude));
                    }
                  });

                  map.fitBounds(bounds);

                  // Force zoom level 5 after bounds adjust
                  window.google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
                    if ((map.getZoom() ?? 0) > 5) {
                      map.setZoom(5);
                    }
                  });
                }
              }
              handleMapLoad();
            }}
            options={mapOptions}
          >
            {/* {currentlocation and zoomin and zoomout} */}
            <div className="absolute right-4 top-4 z-0 flex flex-col items-end gap-8 p-16">
              <div
                onClick={() => {
                  if (!userLocation) return;
                  setCurrentLocationClicked(true);
                  setSearchInput('');
                  setSlectedPlace(undefined);
                  setPreservedQuery('');
                  window.history.pushState(null, '', '/find-stores/');

                  // Use hardcoded coords
                  if (navigator?.geolocation) {
                    navigator.geolocation.getCurrentPosition((pos) => {
                      const coords = { lat: pos?.coords?.latitude, lng: pos?.coords?.longitude };
                      setUserLocation(coords);
                      setMapCenter(coords);
                      mapRef.current?.panTo(coords);
                      mapRef.current?.setZoom(14);
                    });
                  }
                }}
                className=" flex h-40 w-80 items-center justify-center rounded-full bg-white"
              >
                {' '}
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {' '}
                  <path
                    d="M6.7336 9.49176L8.36234 14.3058C8.66764 15.2082 9.90566 15.2384 10.2527 14.3519L14.9253 2.41641C15.2504 1.5861 14.4576 0.75555 13.6373 1.06691L1.65768 5.61356C0.78225 5.94581 0.780297 7.21042 1.65469 7.54549L6.7336 9.49176Z"
                    stroke="black"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />{' '}
                </svg>{' '}
              </div>
              <div className="flex items-center overflow-hidden rounded-full bg-white">
                <div
                  className="flex h-40 w-40 cursor-pointer items-center justify-center"
                  onClick={() => {
                    if (mapRef.current) {
                      const newZoom = (mapRef.current.getZoom() ?? 5) - 1;
                      if (selectedPlace?.location) {
                        mapRef.current.panTo(selectedPlace?.location);
                      }
                      mapRef.current.setZoom(newZoom);
                      setMapZoom(newZoom);
                    }
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 14 2" fill="none">
                    <path d="M0.636032 1L6.99999 1L13.364 1" stroke="#1E1E1E" strokeWidth="2" />
                  </svg>
                </div>

                {/* ➕ Zoom In */}
                <div
                  className="flex h-40 w-40 cursor-pointer items-center justify-center"
                  onClick={() => {
                    if (mapRef.current) {
                      const newZoom = (mapRef.current.getZoom() ?? 5) + 1;
                      if (selectedPlace?.location) {
                        mapRef.current.panTo(selectedPlace?.location);
                      }
                      mapRef.current.setZoom(newZoom);
                      setMapZoom(newZoom);
                    }
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M7.00001 0.63604L7.00001 7M7.00001 7L0.636048 7M7.00001 7L7.00001 13.364M7.00001 7L13.364 7"
                      stroke="#1E1E1E"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {storeData?.map((neighborhood: Store, index: number) => {
              const isSelected = String(selectedPlace?.id) === String(neighborhood?.ID);

              // Use highlighted marker for the selected place, fallback to regular map icons
              const iconToUse = isUltraLargeTablet ? mapIcon : mobileMapICon;

              return (
                <MarkerF
                  key={`${index}_marker`}
                  icon={{
                    url: iconToUse?.svgUrl,
                    scaledSize: {
                      width: isUltraLargeTablet
                        ? iconToUse?.iconElement?.iconWidthDesktopPx || 32
                        : iconToUse?.iconElement?.iconWidthMobilePx || 24,
                      height: isUltraLargeTablet
                        ? iconToUse?.iconElement?.iconHeightDesktopPx || 32
                        : iconToUse?.iconElement?.iconHeightMobilePx || 24,
                    } as google.maps.Size,
                  }}
                  position={{
                    lat: neighborhood?.latitude,
                    lng: neighborhood?.longitude,
                  }}
                  title=""
                  onClick={() => handleMarkerClick(neighborhood)}
                />
              );
            })}

            {/* InfoWindow disabled for city search initial state; overlay renders outside map when selected */}
          </GoogleMap>
        )}
        {!isCitySearch && isOpen && selectedPlace && mapRef?.current && isUltraLargeTablet && (
          <div className="absolute z-0 m-[1rem]">
            <CustomOverlay
              onClose={handleCloseClick}
              isDesktopSize={isUltraLargeTablet}
              parentClassName="relative  max-h-[100vh] w-[370px] overflow-y-scroll rounded-md border-2 border-[#272B5C] bg-white px-12 pb-16 pt-24 shadow-lg"
            >
              <div className="flex flex-col gap-24 text-base" style={{ pointerEvents: 'auto' }}>
                <div className="flex flex-col gap-y-16 font-medium">
                  <div className="flex flex-col items-stretch gap-y-16">
                    <p className="font-body text-16 font-bold uppercase leading-[100%] text-[#2C2C2C]">
                      {infoDetails?.[0]?.name}
                    </p>
                    {distance ? (
                      <p className="ml-4 font-body text-16 font-bold lowercase leading-[100%]">
                        {distance.toFixed(1)} mi
                      </p>
                    ) : null}
                  </div>

                  <div className="flex flex-col gap-y-8">
                    {infoDetails?.[0]?.address1 &&
                    infoDetails?.[0]?.address2 &&
                    infoDetails?.[0]?.city &&
                    infoDetails?.[0]?.postalCode &&
                    infoDetails?.[0]?.stateCode &&
                    infoDetails?.[0]?.countryCode ? (
                      <div>
                        <p className="address font-body text-16 font-normal leading-[130%] text-[#757575]">
                          {infoDetails?.[0]?.address1}, {infoDetails?.[0]?.address2}
                        </p>
                        <p className="city font-body text-16 font-normal leading-[130%] text-[#757575]">
                          {infoDetails?.[0]?.city}, {infoDetails?.[0]?.postalCode}, {infoDetails?.[0]?.stateCode},{' '}
                          {infoDetails?.[0]?.countryCode}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="address font-body text-16 font-normal leading-[130%] text-[#757575]">
                          {storeTimingsData?.[infoDetails[0]?.ID]?.address}
                        </p>
                      </div>
                    )}

                    <div className="flex items-center gap-x-4">
                      <p className="font-body text-[16px] font-bold leading-[130%] text-[#1E1E1E]">
                        {timings ? (timings?.open_now ? 'Open' : 'Closed') : 'Timings'}
                      </p>
                      <span className="font-body text-16 font-normal leading-[130%] text-[#2C2C2C]">·</span>
                      <p className="font-body text-16 font-normal leading-[130%] text-[#2C2C2C]">
                        {timings
                          ? timings?.open_now
                            ? `Closes at ${timings?.closeTime}`
                            : `Opens at ${timings?.openTime}`
                          : 'Not available'}
                      </p>
                    </div>
                  </div>

                  {timings?.open_now && (
                    <div className="flex items-start gap-x-8">
                      <div className="pt-3">
                        {clickAndCollect?.iconElement && clickAndCollect?.svgUrl && (
                          <img
                            src={clickAndCollect?.svgUrl}
                            alt={clickAndCollect?.iconElement?.iconName || 'Search Icon'}
                            style={{
                              width: isUltraLargeTablet
                                ? `${clickAndCollect?.iconElement?.iconWidthDesktopPx}px`
                                : `${clickAndCollect?.iconElement?.iconWidthMobilePx}px`,
                              height: isUltraLargeTablet
                                ? `${clickAndCollect?.iconElement?.iconHeightDesktopPx}px`
                                : `${clickAndCollect?.iconElement?.iconHeightMobilePx}px`,
                            }}
                          />
                        )}
                      </div>
                      <div className="flex flex-col gap-y-8">
                        <p className="font-body text-16 font-bold leading-[100%] text-[#1E1E1E]">Click + Collect</p>
                        <p className="font-body text-16 font-normal leading-[100%] text-[#1E1E1E]">
                          Available from this store
                        </p>
                      </div>
                    </div>
                  )}
                  <div
                    className="group w-fit cursor-pointer items-center justify-center gap-8 rounded-sm border bg-[#2C2C2C] px-12  py-16 hover:bg-[#1e1e1e]"
                    onClick={() => {
                      if (userLocation && infoDetails?.[0]?.latitude && infoDetails?.[0]?.longitude) {
                        const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${userLocation?.lat},${userLocation?.lng}&destination=${infoDetails?.[0]?.latitude},${infoDetails?.[0]?.longitude}&travelmode=driving`;
                        window.open(mapsUrl, '_blank');
                      } else if (infoDetails?.[0]?.latitude && infoDetails?.[0]?.longitude) {
                        const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${infoDetails?.[0]?.latitude},${infoDetails?.[0]?.longitude}&travelmode=driving`;
                        window.open(mapsUrl, '_blank');
                      } else {
                        alert('Store location not available');
                      }
                    }}
                  >
                    <p className="font-body text-16 font-bold leading-[100%] text-white group-hover:text-white">
                      Get Directions
                    </p>
                  </div>
                </div>
                {storeImageUrl && (
                  <div className="flex  w-full items-stretch">
                    <img
                      className=" aspect-[16/9] h-full w-full  items-stretch object-cover"
                      src={storeImageUrl}
                      alt={amplienceStore?.storeImage?.name || infoDetails[0]?.name || 'Store Image'}
                    />
                  </div>
                )}
                {storeBadges?.length > 0 && (
                  <div className="flex w-fit flex-col gap-y-8">
                    <StoreBadges
                      badges={storeBadges}
                      isUltraLargeTablet={isUltraLargeTablet}
                      isMobile={isMobile}
                      selectedLocale={selectedLocale}
                    />
                  </div>
                )}

                <div className="flex flex-col items-start gap-y-16">
                  <p className="font-body text-16 font-bold uppercase leading-[100%]">Hours</p>
                  <div className="weekwise w-full gap-2 font-normal">
                    {weekwiseTimings?.map(({ day, time }: any, index: number) => (
                      <div key={`${index}_time`} className="flex justify-between">
                        <span className="font-body text-16  leading-[130%] text-[#757575]">{day}</span>
                        <div className="flex gap-x-4">
                          <span className="font-body text-16  leading-[130%] text-[#757575]">{time?.openTime}</span>
                          <span className="font-body text-16  leading-[130%] text-[#757575]">-</span>
                          <span className="font-body text-16  leading-[130%] text-[#757575]">{time?.closeTime}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col items-start gap-y-16">
                  <p className="font-body text-16 font-bold uppercase leading-[100%]">{'Contact Us'}</p>
                  <div className="flex flex-col gap-y-4">
                    <div className="flex items-center gap-x-8 ">
                      {phoneIcon?.iconElement && phoneIcon?.svgUrl && (
                        <img
                          src={phoneIcon?.svgUrl}
                          alt={phoneIcon?.iconElement?.iconName || 'Phone Icon'}
                          style={{
                            width: isUltraLargeTablet
                              ? `${phoneIcon?.iconElement?.iconWidthDesktopPx}px`
                              : `${phoneIcon?.iconElement?.iconWidthMobilePx}px`,
                            height: isUltraLargeTablet
                              ? `${phoneIcon?.iconElement?.iconHeightDesktopPx}px`
                              : `${phoneIcon?.iconElement?.iconHeightMobilePx}px`,
                          }}
                        />
                      )}
                      <p className="font-body text-16 font-normal leading-[130%] underline">
                        {infoDetails?.[0]?.phone
                          ? infoDetails?.[0]?.phone
                          : storeTimingsData?.[infoDetails[0]?.ID]?.phone
                          ? storeTimingsData?.[infoDetails[0]?.ID]?.phone
                          : 'Not available'}
                      </p>
                    </div>
                    <div className="flex items-center gap-x-8">
                      {emailIcon?.iconElement && emailIcon?.svgUrl && (
                        <img
                          src={emailIcon?.svgUrl}
                          alt={emailIcon?.iconElement?.iconName || 'Email Icon'}
                          style={{
                            width: isUltraLargeTablet
                              ? `${emailIcon?.iconElement?.iconWidthDesktopPx}px`
                              : `${emailIcon?.iconElement?.iconWidthMobilePx}px`,
                            height: isUltraLargeTablet
                              ? `${emailIcon?.iconElement?.iconHeightDesktopPx}px`
                              : `${emailIcon?.iconElement?.iconHeightMobilePx}px`,
                          }}
                        />
                      )}
                      <p className="font-body text-16 font-normal leading-[130%] underline">
                        {infoDetails?.[0]?.email
                          ? infoDetails?.[0]?.email
                          : storeTimingsData?.[infoDetails[0]?.ID]?.email
                          ? storeTimingsData?.[infoDetails[0]?.ID]?.email
                          : 'Not available'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CustomOverlay>
          </div>
        )}
        {!isCitySearch && isOpen && selectedPlace && mapRef?.current && !isUltraLargeTablet && (
          <div className="z-1 pointer-events-none absolute inset-x-0 m-[1rem] flex justify-center">
            <CustomOverlay
              onClose={handleCloseClick}
              isDesktopSize={isUltraLargeTablet}
              parentClassName="relative  z-10 w-[342px] overflow-y-scroll rounded-md border-2 border-[#272B5C] bg-white px-12 pb-16 pt-24 shadow-lg pointer-events-auto"
            >
              <div className="flex flex-col gap-24 text-base" style={{ pointerEvents: 'auto' }}>
                <div className="flex flex-col gap-y-16 font-medium">
                  <div className="flex flex-col items-stretch gap-y-16">
                    <p className="font-body text-16 font-bold uppercase leading-[100%] text-[#2C2C2C]">
                      {infoDetails?.[0]?.name}
                    </p>
                    {distance ? (
                      <p className="ml-4 font-body text-16 font-bold lowercase leading-[100%]">
                        {distance.toFixed(1)} mi
                      </p>
                    ) : null}
                  </div>

                  <div className="flex flex-col gap-y-8">
                    {infoDetails?.[0]?.address1 &&
                    infoDetails?.[0]?.address2 &&
                    infoDetails?.[0]?.city &&
                    infoDetails?.[0]?.postalCode &&
                    infoDetails?.[0]?.stateCode &&
                    infoDetails?.[0]?.countryCode ? (
                      <div>
                        <p className="address font-body text-16 font-normal leading-[130%] text-[#757575]">
                          {infoDetails?.[0]?.address1}, {infoDetails?.[0]?.address2}
                        </p>
                        <p className="city font-body text-16 font-normal leading-[130%] text-[#757575]">
                          {infoDetails?.[0]?.city}, {infoDetails?.[0]?.postalCode}, {infoDetails?.[0]?.stateCode},{' '}
                          {infoDetails?.[0]?.countryCode}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="address font-body text-16 font-normal leading-[130%] text-[#757575]">
                          {storeTimingsData?.[infoDetails[0]?.ID]?.address}
                        </p>
                      </div>
                    )}

                    <div className="flex items-center gap-x-4">
                      <p className="font-body text-[16px] font-bold leading-[130%] text-[#1E1E1E]">
                        {timings ? (timings?.open_now ? 'Open' : 'Closed') : 'Timings'}
                      </p>
                      <span className="font-body text-16 font-normal leading-[130%] text-[#2C2C2C]">·</span>
                      <p className="font-body text-16 font-normal leading-[130%] text-[#2C2C2C]">
                        {timings
                          ? timings?.open_now
                            ? `Closes at ${timings?.closeTime}`
                            : `Opens at ${timings?.openTime}`
                          : 'Not available'}
                      </p>
                    </div>
                  </div>

                  {timings?.open_now && (
                    <div className="flex items-start gap-x-8">
                      <div className="pt-3">
                        {clickAndCollect?.iconElement && clickAndCollect?.svgUrl && (
                          <img
                            src={clickAndCollect?.svgUrl}
                            alt={clickAndCollect?.iconElement?.iconName || 'Search Icon'}
                            style={{
                              width: isUltraLargeTablet
                                ? `${clickAndCollect?.iconElement?.iconWidthDesktopPx}px`
                                : `${clickAndCollect?.iconElement?.iconWidthMobilePx}px`,
                              height: isUltraLargeTablet
                                ? `${clickAndCollect?.iconElement?.iconHeightDesktopPx}px`
                                : `${clickAndCollect?.iconElement?.iconHeightMobilePx}px`,
                            }}
                          />
                        )}
                      </div>
                      <div className="flex flex-col gap-y-8">
                        <p className="font-body text-16 font-bold leading-[100%] text-[#1E1E1E]">Click + Collect</p>
                        <p className="font-body text-16 font-normal leading-[100%] text-[#1E1E1E]">
                          Available from this store
                        </p>
                      </div>
                    </div>
                  )}
                  <div
                    className="group w-fit cursor-pointer items-center justify-center gap-8 rounded-sm border bg-[#2C2C2C]  px-12 py-16 hover:bg-[#1e1e1e]"
                    onClick={() => {
                      if (userLocation && infoDetails?.[0]?.latitude && infoDetails?.[0]?.longitude) {
                        const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${userLocation?.lat},${userLocation?.lng}&destination=${infoDetails?.[0]?.latitude},${infoDetails?.[0]?.longitude}&travelmode=driving`;
                        window.open(mapsUrl, '_blank');
                      } else if (infoDetails?.[0]?.latitude && infoDetails?.[0]?.longitude) {
                        const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${infoDetails?.[0]?.latitude},${infoDetails?.[0]?.longitude}&travelmode=driving`;
                        window.open(mapsUrl, '_blank');
                      } else {
                        alert('Store location not available');
                      }
                    }}
                  >
                    <p className="font-body text-16 font-bold leading-[100%] text-white group-hover:text-white">
                      Get Directions
                    </p>
                  </div>
                </div>
                {storeImageUrl && (
                  <div className="flex  w-full items-stretch">
                    <img
                      className=" aspect-[16/9] h-full w-full  items-stretch object-cover"
                      src={storeImageUrl}
                      alt={amplienceStore?.storeImage?.name || infoDetails[0]?.name || 'Store Image'}
                    />
                  </div>
                )}
                {storeBadges?.length > 0 && (
                  <div className="flex w-fit flex-col gap-y-8">
                    <StoreBadges
                      badges={storeBadges}
                      isUltraLargeTablet={isUltraLargeTablet}
                      isMobile={isMobile}
                      selectedLocale={selectedLocale}
                    />
                  </div>
                )}

                <div className="flex flex-col items-start gap-y-16">
                  <p className="font-body text-16 font-bold uppercase leading-[100%]">Hours</p>
                  <div className="weekwise w-full gap-2 font-normal">
                    {weekwiseTimings?.map(({ day, time }: any, index: number) => (
                      <div key={`${index}_time`} className="flex justify-between">
                        <span className="font-body text-16  leading-[130%] text-[#757575]">{day}</span>
                        <div className="flex gap-x-4">
                          <span className="font-body text-16  leading-[130%] text-[#757575]">{time?.openTime}</span>
                          <span className="font-body text-16  leading-[130%] text-[#757575]">-</span>
                          <span className="font-body text-16  leading-[130%] text-[#757575]">{time?.closeTime}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col items-start gap-y-16">
                  <p className="font-body text-16 font-bold uppercase leading-[100%]">{'Contact Us'}</p>
                  <div className="flex flex-col gap-y-4">
                    <div className="flex items-center gap-x-8 ">
                      {phoneIcon?.iconElement && phoneIcon?.svgUrl && (
                        <img
                          src={phoneIcon?.svgUrl}
                          alt={phoneIcon?.iconElement?.iconName || 'Phone Icon'}
                          style={{
                            width: isUltraLargeTablet
                              ? `${phoneIcon?.iconElement?.iconWidthDesktopPx}px`
                              : `${phoneIcon?.iconElement?.iconWidthMobilePx}px`,
                            height: isUltraLargeTablet
                              ? `${phoneIcon?.iconElement?.iconHeightDesktopPx}px`
                              : `${phoneIcon?.iconElement?.iconHeightMobilePx}px`,
                          }}
                        />
                      )}
                      <p className="font-body text-16 font-normal leading-[130%] underline">
                        {infoDetails?.[0]?.phone
                          ? infoDetails?.[0]?.phone
                          : storeTimingsData?.[infoDetails[0]?.ID]?.phone
                          ? storeTimingsData?.[infoDetails[0]?.ID]?.phone
                          : 'Not available'}
                      </p>
                    </div>
                    <div className="flex items-center gap-x-8">
                      {emailIcon?.iconElement && emailIcon?.svgUrl && (
                        <img
                          src={emailIcon?.svgUrl}
                          alt={emailIcon?.iconElement?.iconName || 'Email Icon'}
                          style={{
                            width: isUltraLargeTablet
                              ? `${emailIcon?.iconElement?.iconWidthDesktopPx}px`
                              : `${emailIcon?.iconElement?.iconWidthMobilePx}px`,
                            height: isUltraLargeTablet
                              ? `${emailIcon?.iconElement?.iconHeightDesktopPx}px`
                              : `${emailIcon?.iconElement?.iconHeightMobilePx}px`,
                          }}
                        />
                      )}
                      <p className="font-body text-16 font-normal leading-[130%] underline">
                        {infoDetails?.[0]?.email
                          ? infoDetails?.[0]?.email
                          : storeTimingsData?.[infoDetails[0]?.ID]?.email
                          ? storeTimingsData?.[infoDetails[0]?.ID]?.email
                          : 'Not available'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CustomOverlay>
          </div>
        )}
      </div>
    </LoadScript>
  );
};

export default NeighborhoodDiscovery;
